import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    useTranslator,
    useCurrentLanguage,
    useUrlLocalizer
} from 'tradera-lang';
import {
    fetchCartData,
    removeItemsFromCart,
    setCartItemQuantity,
    confirmCartPurchase,
    startOffPlatformCheckout
} from 'tradera-state/views/cart/actions';
import cookieUtil from 'tradera-utils/cookie';
import SiteSubheader from 'tradera-components/site-subheader/site-subheader';
import Breadcrumbs from 'tradera-components/breadcrumbs/breadcrumbs';
import { formatPrice } from '@tradera/blueprint/utils';
import { LocalLoader } from 'tradera-components/loader';

import TopDescription from '../components/vip/bidding/bin-modal/bin-modal-top-description';
import SellerName from '../components/vip/bidding/bin-modal/bin-modal-seller-name';
import ItemDisplay from '../components/vip/bidding/bin-modal/bin-modal-item-display';
import ShippingDisplay from '../components/vip/bidding/bin-modal/bin-modal-shipping-display';
import PaymentSummary from '../components/vip/bidding/bin-modal/bin-modal-payment-summary';
import BottomButtons from '../components/vip/bidding/bin-modal/bin-modal-bottom-buttons';
import {
    Separator,
    BigSeparator
} from '../components/vip/bidding/bin-modal/bin-modal';
import { CART_ID } from 'tradera-constants/cookies';
import './cart-page.scss';

const mapShippingOption = (ship, t) => {
    return {
        displayText: getShippingDisplayText(ship.shippingType, t),
        cost: ship.shippingCost
    };
};

const hasTraderaPay = paymentOptions =>
    paymentOptions.some(option => option.paymentType === 'Braintree');

const CartPage = () => {
    const cartId = cookieUtil.readCookie(CART_ID);
    const dispatch = useDispatch();
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();

    const { language } = useCurrentLanguage();

    const { isFetching, merchantPreOrders } = useSelector(
        state => state.views.cart
    );
    const preferredCurrency = useSelector(
        state => state.multiCurrency.preferredCurrency
    );

    useEffect(() => {
        dispatch(fetchCartData(cartId));
    }, [dispatch, cartId]);

    const goToSeller = (merchantId, merchantAlias) => {
        window.location = toLocalizedUrl(
            `/profile/items/${merchantId}/${merchantAlias}`
        );
    };

    const onConfirm = order => {
        if (hasTraderaPay(order.paymentOptions)) {
            const shopItems = order.orderRows.map(item => {
                return {
                    itemId: item.itemId,
                    expectedItemPrice: item.unitPrice,
                    quantity: item.quantity
                };
            });
            dispatch(
                confirmCartPurchase(cartId, shopItems, purchaseOrderId => {
                    window.location = `/Shopping/Checkout/SelectPayment?purchaseOrderId=${purchaseOrderId}`;
                })
            );
        } else {
            dispatch(
                startOffPlatformCheckout(order.orderRows[0].itemId, () => {
                    window.location = toLocalizedUrl('/shopping/cartcheckout');
                })
            );
        }
    };

    const isCheckoutDisabled = order => {
        return order.orderRows.some(
            row =>
                row.quantityInShop !== undefined &&
                row.quantity > row.quantityInShop
        );
    };

    const changeItemQuantity = (itemId, quantity) => {
        if (quantity === 0) {
            dispatch(removeItemsFromCart(cartId, [itemId]));
        } else {
            dispatch(setCartItemQuantity(cartId, itemId, quantity));
        }
    };

    return (
        <div className="cartoverview-page text-inter text-dark">
            <SiteSubheader>
                <Breadcrumbs
                    categoryPath={[
                        {
                            id: 1,
                            name: t('cart_breadcrumb_title'),
                            url: location.pathname
                        }
                    ]}
                    inSPA={true}
                />
            </SiteSubheader>
            <div className="site-width pt-4 pt-md-5 position-relative">
                {isFetching && <LocalLoader className="py-5" />}

                {!isFetching && merchantPreOrders.length === 0 && (
                    <div className="w-100 py-5 text-center">
                        {t('cartCheckoutOverview_cartEmpty')}
                    </div>
                )}

                {merchantPreOrders.length > 0 && (
                    <div className="w-100 px-4 px-md-5 pb-4 pb-md-5">
                        <TopDescription
                            titleKey={'checkout_bin_title'}
                            descriptions={[
                                'checkout_bin_description1',
                                'checkout_bin_description2'
                            ]}
                            classes="mb-3 mb-md-4"
                        />
                    </div>
                )}

                {merchantPreOrders.map(order => {
                    const shippingOptions = order.shippingOptions.map(option =>
                        mapShippingOption(option, t)
                    );
                    const totalPrice = order.orderRows.reduce(
                        (sum, row) => sum + row.unitPrice * row.quantity,
                        0
                    );
                    return (
                        <div
                            className="container seller-summary justify-content-center px-4 px-md-5 border gray-300"
                            key={order.id}>
                            <SellerName
                                memberId={order.merchantMemberId}
                                alias={order.merchantAlias}
                                cleanedAlias={order.merchantAlias}
                                hasBackground={true}
                                hasShop={true}
                            />

                            {order.orderRows.map((row, index) => (
                                <ItemDisplay
                                    key={row.itemId}
                                    itemImage={row.imageUrl}
                                    itemTitle={row.title}
                                    formattedPrice={formatPrice(
                                        row.unitPrice,
                                        language,
                                        preferredCurrency
                                    )}
                                    isFirstItem={index === 0}
                                    quantity={row.quantity}
                                    quantityInShop={row.quantityInShop}
                                    onQuantityChange={event =>
                                        changeItemQuantity(
                                            row.itemId,
                                            parseInt(event.target.value)
                                        )
                                    }
                                    itemUrl={toLocalizedUrl(row.itemUrl)}
                                />
                            ))}

                            <Separator />

                            <ShippingDisplay
                                shippingOptions={shippingOptions}
                                preferredCurrency={preferredCurrency}
                            />

                            <Separator />

                            <PaymentSummary
                                formattedPrice={formatPrice(
                                    totalPrice,
                                    language,
                                    preferredCurrency
                                )}
                                shippingOptions={shippingOptions}
                            />

                            <BigSeparator />

                            <BottomButtons
                                leftButton={{
                                    action: () =>
                                        goToSeller(
                                            order.merchantMemberId,
                                            order.merchantAlias
                                        ),
                                    text: t('checkout_bin_keepShopping', {
                                        seller: order.merchantAlias
                                    }),
                                    className: 'btn-primary'
                                }}
                                rightButton={{
                                    action: () => onConfirm(order),
                                    text: hasTraderaPay(order.paymentOptions)
                                        ? t('checkout_bin_title')
                                        : t('cart_offplatform_continue'),
                                    className: 'btn-success',
                                    disabled: isCheckoutDisabled(order)
                                }}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Same logic as in TouchWeb, which should be removed once shipping views are reactified
const getShippingDisplayText = (type, t) => {
    switch (type) {
        case 'Alternative':
            return t('shippingInfo_alternativeShippingOption');
        case 'Bussgods':
            return 'Bussgods';
        case 'DHL':
            return 'DHL';
        case 'FedEx':
            return 'FedEx';
        case 'NotSpecified':
            return t('shippingInfo_notSpecified');
        case 'Posten':
            return 'PostNord';
        case 'PostNordParcel':
            return t('shippingInfo_postNordParcel');
        case 'PostNordStamp':
            return t('shippingInfo_postNordStamp');
        case 'SchenkerPrivpak':
            return 'DB Schenker';
        case 'Takeaway':
            return t('shippingInfo_pickup');
        case 'TakeawayOnly':
            return t('shippingInfo_pickupOnly');
        case 'UPS':
            return 'Ups';
        default:
            return type;
    }
};

export default CartPage;
